import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  writeBatch,
  doc
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from '../Variables.js';

Modal.setAppElement('#root');

const Payroll = () => {
  const { userData } = useUserAuth();

  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [activeStaff, setActiveStaff] = useState([]);           // Current active
  const [payrollSnapshot, setPayrollSnapshot] = useState([]);   // Saved month
  const [extrasMap, setExtrasMap] = useState({});               // { staffId: [extras] }
  const [ranks, setRanks] = useState([]);
  const [salaryComponents, setSalaryComponents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [viewingStaff, setViewingStaff] = useState(null);
  const [editingExtras, setEditingExtras] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isExtrasModalOpen, setIsExtrasModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const staffsCollectionRef = collection(db, "users");
  const ranksCollectionRef = collection(db, "ranks");
  const salaryStructureCollectionRef = collection(db, "salaryStructure");
  const payrollRecordsCollectionRef = collection(db, "payrollRecords"); // New immutable collection

  useEffect(() => {
    const now = new Date();
    setSelectedMonth(String(now.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(now.getFullYear());
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setIsFetching(true);
    try {
      await Promise.all([
        fetchStaff(),
        fetchRanks(),
        fetchSalaryComponents()
      ]);
      await fetchPayrollForMonth();
    } catch (err) {
      setError("Failed to load data");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchStaff = async () => {
    const q = query(staffsCollectionRef, where("isActive", "==", true));
    const snap = await getDocs(q);
    const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setActiveStaff(list);
    const depts = [...new Set(list.map(s => s.department).filter(Boolean))];
    setDepartments(depts);
  };

  const fetchRanks = async () => {
    const snap = await getDocs(ranksCollectionRef);
    setRanks(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const fetchSalaryComponents = async () => {
    const snap = await getDocs(salaryStructureCollectionRef);
    setSalaryComponents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const fetchPayrollForMonth = async () => {
    if (!selectedMonth || !selectedYear) return;

    const monthYear = `${selectedYear}-${selectedMonth}`;
    const q = query(payrollRecordsCollectionRef, where("monthYear", "==", monthYear));
    const snap = await getDocs(q);

    const snapshotList = [];
    const extras = {};

    snap.forEach((doc) => {
      const data = doc.data();
      snapshotList.push({
        staffId: data.staffId,
        snapshot: data.staffSnapshot,
        extras: data.extras || [],
      });
      extras[data.staffId] = data.extras || [];
    });

    setPayrollSnapshot(snapshotList);
    setExtrasMap(extras);
  };

  // Re-fetch when month/year changes
  useEffect(() => {
    fetchPayrollForMonth();
  }, [selectedMonth, selectedYear]);

  const getRank = (rankId) => ranks.find(r => r.id === rankId) || {};
  const getComponent = (compId) => salaryComponents.find(c => c.id === compId) || {};

  const calculatePay = (staffMember) => {
    const rank = getRank(staffMember.rank);
    const basic = parseFloat(rank.basicSalary) || 0;
    let gross = basic;
    let deductions = 0;
    const items = { basic };

    rank.components?.forEach(c => {
      const comp = getComponent(c.id);
      if (!comp) return;
      const val = parseFloat(c.value) || 0;
      const amt = comp.calculation === 'percentage'
        ? (comp.percentageOf === 'basic' ? basic * val / 100 : gross * val / 100)
        : val;
      if (comp.type === 'addition') {
        gross += amt;
        items[comp.name.toUpperCase()] = amt;
      } else {
        deductions += amt;
        items[comp.name.toUpperCase()] = -amt;
      }
    });

    const extras = extrasMap[staffMember.id] || [];
    extras.forEach(e => {
      const val = parseFloat(e.value) || 0;
      const amt = e.calculation === 'percentage'
        ? (e.percentageOf === 'basic' ? basic * val / 100 : gross * val / 100)
        : val;
      if (e.type === 'addition') {
        gross += amt;
        items[e.name?.toUpperCase() || 'BONUS'] = amt;
      } else {
        deductions += amt;
        items[e.name?.toUpperCase() || 'DEDUCTION'] = -amt;
      }
    });

    const net = gross - deductions;
    return { basic, gross, deductions, net, items };
  };

  // PDF function (unchanged – still works perfectly)
  const addHeader = (doc) => {
    doc.setFillColor(226, 232, 240);
    doc.rect(0, 0, doc.internal.pageSize.width, 50, 'F');
    try { doc.addImage(ShopLogo, 'PNG', 15, 8, 38, 38); } catch (err) { console.log("Logo not loaded"); }
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text(SHOP_NAME.toUpperCase(), 60, 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(SHOP_ADDRESS.toUpperCase(), 60, 28);
    doc.text(SHOP_PHONE, 60, 35);
    doc.setTextColor(0, 0, 0);
  };

  // Generate Individual Pay Slip PDF
  const generateIndividualPaySlip = (staffMember) => {
    const doc = new jsPDF();
    const { gross, deductions, net, items } = calculatePay(staffMember);
    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
    const generatedDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).replace(/,/g, '');

    addHeader(doc);

    // Employee Information Section
    doc.setFillColor(245, 245, 245);
    doc.rect(15, 55, 180, 30, 'F');
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('STAFF INFORMATION', 20, 65);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Name: ${staffMember.firstName.toUpperCase()} ${staffMember.lastName.toUpperCase()}`, 20, 72);
    doc.text(`Department: ${staffMember.department || 'N/A'}`, 20, 77);
    doc.text(`Bank: ${staffMember.bankName || 'N/A'}`, 100, 72);
    doc.text(`Account: ${staffMember.accountNumber || 'N/A'}`, 100, 77);
    doc.text(`Period: ${monthName}, ${selectedYear}`, 150, 77);

    let y = 95;

    // Earnings Table - Only show if there are earnings
    const hasEarnings = Object.entries(items).some(([_, amt]) => amt > 0);

    if (hasEarnings) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('EARNINGS', 15, y);
      y += 8;

      const earningsData = Object.entries(items)
        .filter(([_, amt]) => amt > 0)
        .map(([name, amt], idx) => [
          idx + 1,
          name.toUpperCase(),
          `N${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        ]);

      autoTable(doc, {
        startY: y,
        head: [['S/N', 'Description', 'Amount']],
        body: earningsData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, textColor: 0, },
        headStyles: { 
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 125 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });

      y = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('No earnings for this period', 15, y);
      y += 15;
    }

    // Deductions Table - Only show if there are deductions
    const hasDeductions = Object.entries(items).some(([_, amt]) => amt < 0);

    if (hasDeductions) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('DEDUCTIONS', 15, y);
      y += 8;

      const deductionsData = Object.entries(items)
        .filter(([_, amt]) => amt < 0)
        .map(([name, amt], idx) => [
          idx + 1,
          name.toUpperCase(),
          `N${(-amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        ]);

      autoTable(doc, {
        startY: y,
        head: [['S/N', 'Description', 'Amount']],
        body: deductionsData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, textColor: 0 },
        headStyles: { 
          fillColor: [231, 76, 60],
          textColor: 255,
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 125 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });

      y = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('No deductions for this period', 15, y);
      y += 15;
    }

    // Summary Section
    doc.setFillColor(71, 85, 105); 
    doc.rect(15, y, 180, 28, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('PAYMENT SUMMARY', 20, y + 8);

    doc.setFontSize(10);
    doc.text('Gross Pay:', 120, y + 8);
    doc.text(`N${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 8, { align: 'right' });
    
    doc.text('Total Deductions:', 120, y + 15);
    doc.text(`N${deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 15, { align: 'right' });
    
    doc.setFontSize(14);
    doc.text('Net Pay:', 120, y + 22);
    doc.text(`N${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 22, { align: 'right' });

    // Footer
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.text(`Generated on: ${generatedDate}`, 105, 285, { align: 'center' });

    // Save the PDF
    doc.save(`PaySlip_${staffMember.firstName}_${staffMember.lastName}_${monthName}_${selectedYear}.pdf`);
  };

  // Staff to show: saved snapshot OR current active staff
  const displayStaff = payrollSnapshot.length > 0
    ? payrollSnapshot.map(p => ({
        ...p.snapshot,
        id: p.staffId,
        extras: p.extras
      }))
    : activeStaff;

  const filteredStaff = displayStaff.filter(s =>
    `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getRank(s.rank)?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const staffByDept = (() => {
    const withDept = filteredStaff.filter(s => s.department);
    const withoutDept = filteredStaff.filter(s => !s.department);
    const depts = [...new Set(withDept.map(s => s.department))];

    return [
      ...depts.map(d => ({ name: d, staff: withDept.filter(s => s.department === d) })),
      withoutDept.length ? { name: "Unassigned Department", staff: withoutDept } : null
    ].filter(Boolean);
  })();

  const openView = (staffMember) => {
    setViewingStaff(staffMember);
    setIsViewModalOpen(true);
  };

  const openExtras = (staffMember) => {
    setEditingExtras(staffMember);
    setIsExtrasModalOpen(true);
  };

  // Fixed extras handlers
  const addExtra = () => {
    setExtrasMap(prev => ({
      ...prev,
      [editingExtras.id]: [
        ...(prev[editingExtras.id] || []),
        {
          name: '',
          type: 'addition',
          calculation: 'fixed',
          percentageOf: 'basic',
          value: 0
        }
      ]
    }));
  };

  const updateExtra = (index, field, value) => {
    setExtrasMap(prev => {
      const extras = [...(prev[editingExtras.id] || [])];
      extras[index] = { ...extras[index], [field]: value };
      return { ...prev, [editingExtras.id]: extras };
    });
  };

  const removeExtra = (index) => {
    setExtrasMap(prev => ({
      ...prev,
      [editingExtras.id]: prev[editingExtras.id].filter((_, i) => i !== index)
    }));
  };

  // Fixed savePayroll
  const savePayroll = async () => {
    if (!selectedMonth || !selectedYear) {
      setError("Please select month and year");
      setShowModal(true);
      return;
    }

    const monthYear = `${selectedYear}-${selectedMonth}`;
    setUpdate(true);

    try {
      const batch = writeBatch(db);

      // Delete old records
      const oldQ = query(payrollRecordsCollectionRef, where("monthYear", "==", monthYear));
      const oldSnap = await getDocs(oldQ);
      oldSnap.docs.forEach(d => batch.delete(d.ref));

      // Save snapshot of all active staff
      for (const member of activeStaff) {
        const payload = {
          staffId: member.id,
          monthYear,
          staffSnapshot: { ...member },
          extras: extrasMap[member.id] || [],
          savedAt: Timestamp.now()
        };
        batch.set(doc(payrollRecordsCollectionRef), payload);
      }

      await batch.commit();
      await fetchPayrollForMonth();

      setSuccessMessage(`Payroll saved successfully for ${monthYear}!`);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      setError("Save failed: " + err.message);
      setShowModal(true);
    } finally {
      setUpdate(false);
    }
  };

  if (!userData) return <Loader text="Reading" />;

  const allowedRoles = ['Admin', 'HR', 'Supreme'];
  if (!allowedRoles.includes(userData.status)) {
    return <p className="male-female-stat align-center justify-center">Access denied.</p>;
  }

  return (
    <>
      {update && <Loader text="Saving" />}

      {/* Your entire UI stays EXACTLY the same */}
      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link"><i className="fa-solid fa-arrow-left size3"></i></Link>
        <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Payroll - {selectedMonth}/{selectedYear}</h2>
        <Link to="#" className="home-link" onClick={(e) => { e.preventDefault(); fetchAllData(); }}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>

      <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
        {/* Month/Year + Save button */}
        <div className="row gap-1 align-center justify-center" style={{marginTop: '10px', marginBottom: '10px'}}>
          <select className="input-field" style={{marginBottom: '0px'}} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {/* {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, '0)}>
                {new Date(0, i).toLocaleString('default', { month: 'short' })}
              </option>
            ))} */}
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                {new Date(0, i).toLocaleString('default', { month: 'short' })}
              </option>
            ))}
          </select>
          <input type="number" className="input-field" style={{marginBottom: '0px'}} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} min="2000" />
          <button className="login-button login-button-d caps" style={{marginTop: '0px'}} onClick={savePayroll}>
            {payrollSnapshot.length > 0 ? "Update Payroll" : "Save Payroll"}
          </button>
        </div>

        <input type="text" className="input-field1" placeholder="Search by name, dept, rank..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

        <div className="scroll-fixed scroll-fixed1">
          <div className="scrollable">
            {isFetching ? (
              <p className="loading-text">Loading payroll...</p>
            ) : staffByDept.length === 0 ? (
              <p className="loading-text">No active staff found.</p>
            ) : (
              staffByDept.map(dept => (
                <div key={dept.name} style={{maxWidth: '600px', margin: 'auto'}}>
                  <h3 className="caps size5">{dept.name}</h3>
                  {dept.staff.map(staffMember => {
                    const rank = getRank(staffMember.rank);
                    const { gross, deductions, net } = calculatePay(staffMember);

                    return (
                      <div key={staffMember.id} className="class-item">
                        <p>
                          <strong>{staffMember.firstName} {staffMember.lastName}</strong><br />
                          <small>{rank.name} ({rank.gradeLevel})</small><br />
                          <small>Gross: ₦{parseFloat(gross).toLocaleString()}</small><br />
                          <small>Ded: ₦{parseFloat(deductions).toLocaleString()}</small><br />
                          <strong>Net: ₦{parseFloat(net).toLocaleString()}</strong>
                          {(extrasMap[staffMember.id]?.length || 0) > 0 && (
                            <><br /><span className="font-light-blue-cash bold size5">Items Added</span></>
                          )}
                        </p>
                        <div className="row align-center gap-1">
                          <button className="list-btn" onClick={() => openView(staffMember)}><i className="fas fa-eye"></i></button>
                          <button className="list-btn" onClick={() => generateIndividualPaySlip(staffMember)}><i className="fas fa-print"></i></button>
                          <button className="list-btn" onClick={() => openExtras(staffMember)}><i className="fas fa-edit"></i></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Modal */}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* View Modal – fixed to use extrasMap */}
      <Modal isOpen={isViewModalOpen} onRequestClose={() => setIsViewModalOpen(false)} className="custom-modal modal-scroll" overlayClassName="custom-modal-overlay">
        <div className="modal-scroll">
          <h2 className="caps size5 bold text-center">PAY SLIP ({new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'short', year: 'numeric' })})</h2>
          {viewingStaff && (() => {
            const rank = getRank(viewingStaff.rank);
            const { basic, gross, deductions, net } = calculatePay(viewingStaff);
            const extras = extrasMap[viewingStaff.id] || [];

            return (
              <div>
                <p>
                  <span className="bold">{viewingStaff.firstName} {viewingStaff.lastName}</span><br />
                  <span className="size5">{rank.name || 'No Rank'} ({rank.gradeLevel && `${rank.gradeLevel}`})</span><br />
                  <span className="size5">{viewingStaff.department}</span>
                </p>
                <p>
                  <span className="size5 bold">Net Pay</span><br />
                  <span className="size1 bold">₦{parseFloat(net).toLocaleString()}</span>
                </p>

                {/* Earnings */}
                <div>
                  <p className="caps bold size5 font-teal">EARNINGS</p>
                  <p>
                    <strong>Basic Salary:</strong>&nbsp;<span>₦{parseFloat(basic).toLocaleString()}</span>
                    {/* Rank-based additions */}
                    {rank.components?.filter(c => getComponent(c.id)?.type === 'addition').map((c, i) => {
                      const comp = getComponent(c.id);
                      const val = parseFloat(c.value) || 0;
                      const amount = comp.calculation === 'percentage'
                        ? (comp.percentageOf === 'basic' ? (basic * val) / 100 : (gross * val) / 100)
                        : val;
                      return (
                        <div key={i} className="row justify-between font-green">
                          <br /><span>+ {comp.name}:&nbsp;</span>
                          <span>₦{amount.toLocaleString()}</span>
                        </div>
                      );
                    })}

                    {/* Extra additions */}
                    {extras.filter(e => e.type === 'addition').map((e, i) => {
                      const val = parseFloat(e.value) || 0;
                      const amount = e.calculation === 'percentage'
                        ? (e.percentageOf === 'basic' ? (basic * val) / 100 : (gross * val) / 100)
                        : val;
                      return (
                        <div key={`extra-${i}`} className="row justify-between font-green">
                          <br /><span>+ {e.name || 'Unnamed Bonus'}:&nbsp;</span>
                          <span>₦{amount.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </p>
                
                  <div className="row justify-between">
                    <strong>Gross Pay:&nbsp;</strong>
                    <strong>₦{parseFloat(gross).toLocaleString()}</strong>
                  </div>
                </div>

                {/* Deductions */}
                {deductions > 0 && (
                <div>
                  <p className="caps bold size5 font-red">DEDUCTIONS</p>

                  {/* Rank-based deductions */}
                  {rank.components?.filter(c => getComponent(c.id)?.type === 'subtraction').map((c, i) => {
                    const comp = getComponent(c.id);
                    const val = parseFloat(c.value) || 0;
                    const amount = comp.calculation === 'percentage'
                      ? (comp.percentageOf === 'basic' ? (basic * val) / 100 : (gross * val) / 100)
                      : val;
                    return (
                      <div key={i} className="row justify-between font-red">
                        <br /><span>− {comp.name}:&nbsp;</span>
                        <span>₦{amount.toLocaleString()}</span>
                      </div>
                    );
                  })}

                  {/* Extra deductions */}
                  {extras.filter(e => e.type === 'deduction').map((e, i) => {
                    const val = parseFloat(e.value) || 0;
                    const amount = e.calculation === 'percentage'
                      ? (e.percentageOf === 'basic' ? (basic * val) / 100 : (gross * val) / 100)
                      : val;
                    return (
                      <div key={`ded-${i}`} className="row justify-between font-red">
                        <br /><span>− {e.name || 'Unnamed Deduction'}:&nbsp;</span>
                        <span>₦{amount.toLocaleString()}</span>
                      </div>
                    );
                  })}

                  {deductions > 0 && (
                    <>
                      <br />
                      <div className="row justify-between">
                        <strong>Total Deductions:&nbsp;</strong>
                        <strong>₦{parseFloat(deductions).toLocaleString()}</strong>
                      </div>
                    </>
                  )}
                </div>
                )}
                
                <button 
                  className="login-button caps mt-20" 
                  onClick={() => setIsViewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Extras Modal – now works perfectly */}
      <Modal isOpen={isExtrasModalOpen} onRequestClose={() => { setIsExtrasModalOpen(false); setEditingExtras(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2 className="caps size5">Add Special Items</h2>
        {editingExtras && (
          <div className="modal-scroll">
            <p><strong>{editingExtras.firstName} {editingExtras.lastName}</strong> - {selectedMonth}/{selectedYear}</p>
            {(extrasMap[editingExtras.id] || []).map((extra, i) => (
              <div key={i}>
                <label className="input-label">Name</label>
                <input type="text" className="input-field" placeholder="e.g. Performance Bonus, Loan"
                  value={extra.name} onChange={(e) => updateExtra(i, 'name', e.target.value)} />
                <label className="input-label">Addition/Deduction</label>
                <select className="input-field" value={extra.type} onChange={(e) => updateExtra(i, 'type', e.target.value)}>
                  <option value="addition">Addition</option>
                  <option value="deduction">Deduction</option>
                </select>
                <label className="input-label">Calculation</label>
                <select className="input-field" value={extra.calculation} onChange={(e) => updateExtra(i, 'calculation', e.target.value)}>
                  <option value="fixed">Fixed Amount</option>
                  <option value="percentage">Percentage</option>
                </select>
                {extra.calculation === 'percentage' && (
                  <>
                    <label className="input-label">Percentage of</label>
                    <select className="input-field" value={extra.percentageOf} onChange={(e) => updateExtra(i, 'percentageOf', e.target.value)}>
                      <option value="basic">Basic</option>
                      <option value="gross">Gross</option>
                    </select>
                  </>
                )}
                <label className="input-label">{extra.calculation === 'percentage' ? 'Percentage (%)' : 'Amount (₦)'}</label>
                <input type="number" className="input-field" value={extra.value} onChange={(e) => updateExtra(i, 'value', e.target.value)} />
                <button type="button" className="remove-button" onClick={() => removeExtra(i)}>
                  <i className="fas fa-trash-alt font-red"></i>&nbsp;<span className="size5 font-red bold">DELETE</span>
                </button>
                <hr />
              </div>
            ))}
            <button className="login-button" onClick={addExtra}>Add Item</button>
            <div className="row align-center justify-center gap-15">
              <button className="login-button" onClick={() => { setIsExtrasModalOpen(false); setEditingExtras(null); }}>Close</button>
              <button className="login-button login-button-d" onClick={async () => {
                setIsExtrasModalOpen(false);
                setEditingExtras(null);
                await savePayroll();
              }}>
                Save & Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Payroll;